import {
  capabilityManagePermission,
  getProviderDescriptor,
  isIntegrationCapability,
} from '@aysar/contracts';
import { useTranslation } from 'react-i18next';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import {
  useIntegrationsOverviewQuery,
  useWriteIntegrationConfigMutation,
} from '../../services/integrations/integrations.api';
import {
  providerFieldFor,
  providerFormSchema,
  providerInitialValues,
  toWriteConfigBody,
} from '../../utils/provider-fields/provider-fields.util';

/**
 * One provider's credentials, as a form generated from the **catalog** (N21).
 *
 * Nothing here knows what a Gemini key or an S3 bucket is: the field list, its
 * types and which entries are secret all come from the capability descriptor, so a
 * provider added to `libs/contracts` appears with a working form and no admin code.
 *
 * Secrets open **blank** and a blank secret is not sent, which is the api's
 * blank-keeps-secret rule expressed where the operator can see it: the description
 * under the box names the stored key by its last four so they know something is
 * there.
 */
export function ProviderConfigModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('integrations');
  const toast = useToast();
  const { can } = useHasPermission();

  const capability = typeof modalData?.['capability'] === 'string' ? modalData['capability'] : '';
  const provider = typeof modalData?.['provider'] === 'string' ? modalData['provider'] : '';

  const overview = useIntegrationsOverviewQuery();
  const [writeConfig] = useWriteIntegrationConfigMutation();

  const descriptor = getProviderDescriptor(capability, provider);
  const status = overview.data?.capabilities
    .find((entry) => entry.capability === capability)
    ?.providers.find((entry) => entry.provider === provider);

  // Narrowed rather than cast: the capability arrives as modal DATA, which is a
  // plain string, and an unknown one must fail closed rather than build a
  // permission key nobody holds and hope the api refuses it.
  const mayManage = isIntegrationCapability(capability)
    ? can(capabilityManagePermission(capability))
    : false;

  const fields = descriptor?.fields ?? [];

  return (
    <DynamicFormModal
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={t('config.title', { provider })}
      description={t('config.hint')}
      size="lg"
      // Held until the overview arrives: RHF reads `defaultValues` once, so a form
      // mounted before the stored values are known would open blank and save blank.
      isLoading={overview.isLoading}
      fields={fields.map((field) => providerFieldFor(field, status, t))}
      schema={providerFormSchema(fields, t)}
      initialValues={providerInitialValues(fields, status)}
      submitLabel={t('config.save')}
      onCancel={onCancel}
      onSubmit={async (values) => {
        // Both refusals below are shaped as an `ApiError` rather than thrown
        // bare, because the form renders a thrown error's own message as its
        // failure summary — `new Error('forbidden')` puts that internal token on
        // screen, in every locale (U8). `status: 0` is the literal truth here:
        // the request never left the browser.
        const refuse = (code: string, message: string): never => {
          const refusal: ApiError = { status: 0, code, message };
          throw refusal;
        };

        if (!mayManage) {
          // The api would answer 403; saying so before the round trip is kinder,
          // and the button is already gated — this is the belt to that brace.
          refuse('forbidden', t('config.forbidden'));
        }

        const body = toWriteConfigBody(fields, values);

        if (Object.keys(body.values).length === 0) {
          // Nothing was entered, or what was entered never committed — an
          // unparsed document emits no value, so both arrive here identically.
          // The api's own schema refuses an empty body, and letting it do so
          // shows the operator an untranslated English server string (U8) for
          // the gesture this form's own copy tells them to make: a provider
          // whose only field is a secret is saved by *leaving it blank*.
          refuse('nothing_to_save', t('config.nothingToSave'));
        }

        try {
          await writeConfig({ capability, provider, body }).unwrap();
          toast.success(t('config.saved'));
          onCancel();
        } catch (error) {
          throw toApiError(error);
        }
      }}
    />
  );
}
